import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionLabel } from "@/components/Eyebrow";
import { ArrowRight } from "lucide-react";

export const TOOLS = [
  { id: "eligibility",            num: "I",   to: "/tools/eligibility",            name: "Transfer Eligibility Checker",  blurb: "A read on eligibility, competitiveness, and pathway classification — UF, UCF, FSU, USF, FIU, Harvard." },
  { id: "gpa-forecast",           num: "II",  to: "/tools/gpa-forecast",           name: "GPA Forecast",                  blurb: "Projects your final cumulative GPA against your target university's competitive band." },
  { id: "semester-planner",       num: "III", to: "/tools/semester-planner",       name: "Semester Planner",              blurb: "Auto-sequences four semesters against the Common Prerequisites Manual for your intended major." },
  { id: "probability-dashboard",  num: "IV",  to: "/tools/probability-dashboard",  name: "Transfer Probability Dashboard",blurb: "Composite readiness signal across six dimensions. Not an admissions prediction." },
  { id: "deadlines",              num: "V",   to: "/tools/deadlines",              name: "Deadline Tracker",              blurb: "Curated UF / UCF / FSU / USF / FIU / Harvard deadlines, with personalized 2-4-week strategy commentary." },
  { id: "transcript-audit",       num: "VI",  to: "/tools/transcript-audit",       name: "Transcript Audit",              blurb: "Upload a PDF transcript. We parse it, map prereqs, flag excess credits, and return a full structured audit." },
];

export default function Tools() {
  return (
    <Layout>
      <SEO
        title="Field Instruments — Six Tools for Florida Transfer Students"
        description="Transfer Eligibility Checker, GPA Forecast, Semester Planner, Transfer Probability Dashboard, Deadline Tracker, and Transcript Audit. Six tools. One operating system."
        canonical="/tools"
        keywords="Florida transfer tools, transcript audit, GPA forecast, semester planner, transfer eligibility checker"
      />
      <section className="border-b border-stone/30 bg-bone" data-testid="tools-index-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-16">
          <Eyebrow>Field Instruments</Eyebrow>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[4.75rem] text-ink leading-[1.04] tracking-tightest mt-5">
            Six tools.<br /><span className="italic text-ivy">One operating system.</span>
          </h1>
          <p className="mt-7 text-ink-soft text-base sm:text-lg max-w-3xl leading-relaxed">
            Every output ends with the same honest question: what&rsquo;s the next decision you need to make?
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <SectionLabel index="01" label="The Suite" />
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-stone/40" data-testid="tools-grid">
          {TOOLS.map((t) => (
            <Link key={t.id} to={t.to} className="border-r border-b border-stone/40 bg-bone p-8 hover:bg-bone-dim transition-colors flex flex-col group" data-testid={`tool-card-${t.id}`}>
              <div className="flex items-baseline gap-3"><span className="font-serif text-brass text-3xl leading-none">{t.num}</span><span className="text-[10px] uppercase tracking-[0.22em] text-ivy font-semibold">Live</span></div>
              <h3 className="font-serif font-medium text-xl text-ink mt-5 tracking-tight group-hover:text-ivy transition-colors">{t.name}</h3>
              <p className="text-sm text-ink-soft mt-3 leading-relaxed flex-1">{t.blurb}</p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy group-hover:text-brass transition-colors">Run the tool <ArrowRight className="h-3 w-3" /></div>
            </Link>
          ))}
        </div>
        <div className="mt-12 border-t border-stone/30 pt-8">
          <p className="text-[11px] uppercase tracking-[0.22em] text-stone">Anonymous + free-tier users: 10 tool runs per IP per hour. Platform / Florida Transfer / Selective &amp; Elite members: unlimited.</p>
        </div>
      </section>
    </Layout>
  );
}
